import React, { useState, useEffect, useMemo, useCallback, useDeferredValue, useRef } from 'react';
import { 
  Tv, 
  Film, 
  Layers, 
  Star, 
  Search, 
  RefreshCw, 
  Play, 
  Server, 
  Filter, 
  Radio, 
  ChevronRight, 
  ChevronDown,
  Clock, 
  Calendar, 
  X, 
  Info,
  CheckCircle2,
  AlertCircle,
  List,
  Plus,
  Edit2,
  Trash2,
  Globe,
  User,
  Settings,
  Check
} from 'lucide-react';
import { 
  IptvSource, 
  IptvChannel, 
  IptvVodMovie, 
  IptvSeries, 
  IptvFavorite, 
  IptvSyncProgress, 
  DeviceInfo 
} from '../../types';
import { 
  syncIptvSource, 
  loadCachedIptvData, 
  fetchXtreamSeriesInfo,
  getProxiedStreamUrl,
  getProxiedImageUrl,
  clearCachedSource,
  testIptvConnection
} from '../../services/iptvService';
import { 
  subscribeToIptvSources, 
  subscribeToIptvFavorites, 
  toggleIptvFavorite,
  saveIptvSource,
  deleteIptvSource
} from '../../lib/firebase';
import { IptvPlayer } from './IptvPlayer';
import { ChannelLogo } from './ChannelLogo';
import { MarqueeText } from './MarqueeText';

const CHUNK_SIZE = 80;

interface IptvViewProps {
  userId?: string;
  deviceInfo?: DeviceInfo;
  onOpenAdmin?: () => void;
}

export const IptvView: React.FC<IptvViewProps> = ({
  userId,
  deviceInfo,
  onOpenAdmin
}) => {
  // Navigation & Tabs
  const [activeTab, setActiveTab] = useState<'live' | 'movies' | 'series' | 'favorites' | 'sources'>('live');
  const [selectedSourceId, setSelectedSourceId] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const deferredSearchQuery = useDeferredValue(searchQuery);

  // Custom Dropdown Open States for Remote/TV & Touch
  const [isSourceDropdownOpen, setIsSourceDropdownOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const sourceDropdownRef = useRef<HTMLDivElement | null>(null);
  const categoryDropdownRef = useRef<HTMLDivElement | null>(null);

  // Close dropdowns on outside click or escape
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (sourceDropdownRef.current && !sourceDropdownRef.current.contains(e.target as Node)) {
        setIsSourceDropdownOpen(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target as Node)) {
        setIsCategoryDropdownOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsSourceDropdownOpen(false);
        setIsCategoryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Pagination / Chunking
  const [visibleCount, setVisibleCount] = useState<number>(CHUNK_SIZE);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // Data State
  const [sources, setSources] = useState<IptvSource[]>([]);
  const [channels, setChannels] = useState<IptvChannel[]>([]);
  const [movies, setMovies] = useState<IptvVodMovie[]>([]);
  const [seriesList, setSeriesList] = useState<IptvSeries[]>([]);
  const [favorites, setFavorites] = useState<IptvFavorite[]>([]);

  // Source Edit / Add Modal State
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [editingSource, setEditingSource] = useState<Partial<IptvSource> | null>(null);
  const [isTestingSource, setIsTestingSource] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; userInfo?: any; serverInfo?: any } | null>(null);
  const [syncingSourceId, setSyncingSourceId] = useState<string | null>(null);

  // Player & Detail Modal State
  const [activePlayingItem, setActivePlayingItem] = useState<IptvChannel | IptvVodMovie | any | null>(null);
  const [selectedSeriesDetail, setSelectedSeriesDetail] = useState<IptvSeries | null>(null);
  const [loadingSeriesDetail, setLoadingSeriesDetail] = useState(false);

  // Auto-focus and scroll inside dropdown menus when opened
  useEffect(() => {
    if (isCategoryDropdownOpen) {
      setTimeout(() => {
        const drop = categoryDropdownRef.current;
        if (!drop) return;
        const selectedOption = drop.querySelector<HTMLElement>('.bg-red-600, #iptv-category-option-all') ||
                               drop.querySelector<HTMLElement>('[data-focusable="true"]');
        if (selectedOption) {
          selectedOption.focus();
          selectedOption.classList.add('tv-focused');
          selectedOption.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }, 50);
    }
  }, [isCategoryDropdownOpen]);

  useEffect(() => {
    if (isSourceDropdownOpen) {
      setTimeout(() => {
        const drop = sourceDropdownRef.current;
        if (!drop) return;
        const selectedOption = drop.querySelector<HTMLElement>('.bg-red-600, [id^="iptv-source-option-"]') ||
                               drop.querySelector<HTMLElement>('[data-focusable="true"]');
        if (selectedOption) {
          selectedOption.focus();
          selectedOption.classList.add('tv-focused');
          selectedOption.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }, 50);
    }
  }, [isSourceDropdownOpen]);

  // Hierarchical single-step back navigation for IPTV (remote control & D-pad)
  const handleIptvBack = useCallback((): boolean => {
    // 0. If IptvPlayer has active sub-modal (settings or channel list), let it handle
    if (typeof (window as any).__handleIptvPlayerBack === 'function') {
      const handled = (window as any).__handleIptvPlayerBack();
      if (handled) return true;
    }

    // 1. If video player is open, close player and restore focus to channel/movie card
    if (activePlayingItem) {
      const returningId = activePlayingItem.id;
      setActivePlayingItem(null);
      setTimeout(() => {
        const chCard = document.getElementById(`iptv-ch-${returningId}`) ||
                       document.getElementById(`iptv-movie-${returningId}`) ||
                       document.querySelector<HTMLElement>('[id^="iptv-ch-"], [id^="iptv-movie-"]');
        if (chCard) {
          chCard.focus();
          chCard.classList.add('tv-focused');
          chCard.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }, 80);
      return true;
    }

    // 2. If series detail modal is open, close it
    if (selectedSeriesDetail) {
      const seriesId = selectedSeriesDetail.id;
      setSelectedSeriesDetail(null);
      setTimeout(() => {
        const seriesCard = document.getElementById(`iptv-series-${seriesId}`) ||
                           document.querySelector<HTMLElement>('[id^="iptv-series-"]');
        if (seriesCard) {
          seriesCard.focus();
          seriesCard.classList.add('tv-focused');
          seriesCard.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }, 80);
      return true;
    }

    // 3. If source add/edit modal is open, close it
    if (showSourceModal) {
      setShowSourceModal(false);
      setEditingSource(null);
      return true;
    }

    // 4. If category dropdown is open, close it and focus trigger
    if (isCategoryDropdownOpen) {
      setIsCategoryDropdownOpen(false);
      const catBtn = document.getElementById('iptv-category-selector-btn');
      if (catBtn) {
        catBtn.focus();
        catBtn.classList.add('tv-focused');
      }
      return true;
    }

    // 5. If source dropdown is open, close it and focus trigger
    if (isSourceDropdownOpen) {
      setIsSourceDropdownOpen(false);
      const srcBtn = document.getElementById('iptv-source-selector-btn');
      if (srcBtn) {
        srcBtn.focus();
        srcBtn.classList.add('tv-focused');
      }
      return true;
    }

    // 6. If search bar has text, clear search
    if (searchQuery.trim() !== '') {
      setSearchQuery('');
      const searchInput = document.getElementById('iptv-search-input');
      if (searchInput) {
        searchInput.focus();
        searchInput.classList.add('tv-focused');
      }
      return true;
    }

    // 7. If filtered by a specific category, return to all categories
    if (selectedCategory !== 'all') {
      setSelectedCategory('all');
      const catBtn = document.getElementById('iptv-category-selector-btn');
      if (catBtn) {
        catBtn.focus();
        catBtn.classList.add('tv-focused');
      }
      return true;
    }

    // 8. If on a sub-tab (movies, series, favorites, sources), return to 'live' tab
    if (activeTab !== 'live') {
      setActiveTab('live');
      const liveTab = document.getElementById('tab-iptv-live');
      if (liveTab) {
        liveTab.focus();
        liveTab.classList.add('tv-focused');
      }
      return true;
    }

    // 9. If already at the root of IPTV ('live' tab, category 'all', no search, no player), return false so App.tsx returns to Home
    return false;
  }, [
    activePlayingItem,
    selectedSeriesDetail,
    showSourceModal,
    isCategoryDropdownOpen,
    isSourceDropdownOpen,
    searchQuery,
    selectedCategory,
    activeTab
  ]);

  // Expose to window for global spatial navigation and Android bridge
  useEffect(() => {
    (window as any).__handleIptvBack = handleIptvBack;
    return () => {
      delete (window as any).__handleIptvBack;
    };
  }, [handleIptvBack]);

  // Syncing State
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const [syncProgress, setSyncProgress] = useState<IptvSyncProgress | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  // Reset pagination when category, tab, source, or search changes
  useEffect(() => {
    setVisibleCount(CHUNK_SIZE);
  }, [activeTab, selectedSourceId, selectedCategory, deferredSearchQuery]);

  // IntersectionObserver for infinite scrolling performance
  useEffect(() => {
    if (!loadMoreRef.current) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setVisibleCount((prev) => prev + CHUNK_SIZE);
      }
    }, { threshold: 0.1, rootMargin: '300px' });

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [activeTab, visibleCount]);

  const hasAttemptedAutoSyncRef = useRef(false);

  // 1. Subscribe to IPTV Sources and Favorites
  useEffect(() => {
    const unsubSources = subscribeToIptvSources(userId, (newSources) => {
      setSources(newSources);
    });

    const unsubFavs = userId 
      ? subscribeToIptvFavorites(userId, (favs) => setFavorites(favs))
      : () => {};

    // Load initial cached items
    loadCachedIptvData().then((cached) => {
      setChannels(cached.channels || []);
      setMovies(cached.movies || []);
      setSeriesList(cached.series || []);
      setInitialLoading(false);
    }).catch(() => {
      setInitialLoading(false);
    });

    return () => {
      unsubSources();
      unsubFavs();
    };
  }, [userId]);

  // Auto-sync only ONCE on initial mount if cache is empty and sources exist
  useEffect(() => {
    if (!initialLoading && !hasAttemptedAutoSyncRef.current && channels.length === 0 && sources.length > 0 && !isSyncingAll) {
      hasAttemptedAutoSyncRef.current = true;
      handleSyncAllSources();
    }
  }, [initialLoading, sources.length, channels.length, isSyncingAll]);

  // Handle Synchronizing all sources
  const handleSyncAllSources = async () => {
    if (sources.length === 0 || isSyncingAll) return;
    setIsSyncingAll(true);
    setSyncProgress(null);

    const allCh: IptvChannel[] = [];
    const allMv: IptvVodMovie[] = [];
    const allSr: IptvSeries[] = [];

    try {
      for (const src of sources) {
        if (!src.enabled) continue;
        try {
          const res = await syncIptvSource(src, (prog) => {
            setSyncProgress(prog);
          });
          allCh.push(...res.channels);
          allMv.push(...res.movies);
          allSr.push(...res.series);

          // Update source metadata
          saveIptvSource({
            ...src,
            lastSyncedAt: new Date().toISOString(),
            liveCount: res.channels.length,
            moviesCount: res.movies.length,
            seriesCount: res.series.length,
            status: 'active',
            lastError: undefined
          }, userId).catch(() => {});
        } catch (srcErr: any) {
          console.warn(`Error syncing source ${src.name}:`, srcErr);
          saveIptvSource({
            ...src,
            status: 'error',
            lastError: srcErr.message || 'Error en descarregar la llista'
          }, userId).catch(() => {});
        }
      }

      if (allCh.length > 0 || allMv.length > 0 || allSr.length > 0) {
        setChannels(allCh);
        setMovies(allMv);
        setSeriesList(allSr);
      } else {
        // Fallback: check what is saved in indexedDB cache
        const cached = await loadCachedIptvData();
        setChannels(cached.channels || []);
        setMovies(cached.movies || []);
        setSeriesList(cached.series || []);
      }
    } catch (err) {
      console.error('Sync all IPTV sources error:', err);
    } finally {
      setIsSyncingAll(false);
      setTimeout(() => setSyncProgress(null), 3000);
    }
  };

  // Sync a single source
  const handleSyncSingleSource = async (source: IptvSource) => {
    if (syncingSourceId) return;
    setSyncingSourceId(source.id);
    try {
      const res = await syncIptvSource(source, (prog) => {
        setSyncProgress(prog);
      });

      await saveIptvSource({
        ...source,
        lastSyncedAt: new Date().toISOString(),
        liveCount: res.channels.length,
        moviesCount: res.movies.length,
        seriesCount: res.series.length,
        status: 'active',
        lastError: undefined
      }, userId);

      // Refresh cached dataset
      const refreshed = await loadCachedIptvData();
      setChannels(refreshed.channels || []);
      setMovies(refreshed.movies || []);
      setSeriesList(refreshed.series || []);
    } catch (err: any) {
      console.error(`Error syncing source ${source.name}:`, err);
      await saveIptvSource({
        ...source,
        status: 'error',
        lastError: err.message || 'Error en sincronitzar la font IPTV'
      }, userId);
    } finally {
      setSyncingSourceId(null);
      setTimeout(() => setSyncProgress(null), 3000);
    }
  };

  // Delete an IPTV source and its cache
  const handleDeleteSource = async (sourceId: string, sourceName: string) => {
    if (!window.confirm(`Segur que vols eliminar la llista "${sourceName}"? Es suprimiran els canals associats de la memòria cau.`)) {
      return;
    }
    try {
      await clearCachedSource(sourceId);
      await deleteIptvSource(sourceId, userId);
      const refreshed = await loadCachedIptvData();
      setChannels(refreshed.channels || []);
      setMovies(refreshed.movies || []);
      setSeriesList(refreshed.series || []);
    } catch (err) {
      console.error('Error deleting source:', err);
    }
  };

  // Save new / edited source
  const handleSaveSource = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSource || !editingSource.name) return;

    try {
      const isNew = !editingSource.id;
      const sourceToSave: IptvSource = {
        id: editingSource.id || `iptv_${Date.now()}`,
        name: editingSource.name,
        type: editingSource.type || 'm3u',
        url: editingSource.url || '',
        serverUrl: editingSource.serverUrl || '',
        username: editingSource.username || '',
        password: editingSource.password || '',
        userAgent: (editingSource.userAgent || 'IPTVSmartersPro').trim(),
        enabled: editingSource.enabled ?? true,
        status: 'idle',
        createdAt: editingSource.createdAt || new Date().toISOString()
      };

      await saveIptvSource(sourceToSave, userId);
      setShowSourceModal(false);
      setEditingSource(null);
      setTestResult(null);

      // Auto-sync the saved source
      handleSyncSingleSource(sourceToSave);
    } catch (err: any) {
      alert(`Error en desar la llista: ${err.message}`);
    }
  };

  // Test source connection
  const handleTestSource = async () => {
    if (!editingSource) return;
    setIsTestingSource(true);
    setTestResult(null);
    try {
      const res = await testIptvConnection(editingSource);
      setTestResult({
        success: res.success,
        message: res.message
      });
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.message || 'Error de connexió'
      });
    } finally {
      setIsTestingSource(false);
    }
  };

  // Toggle favorite for an item
  const handleToggleFav = async (
    item: IptvChannel | IptvVodMovie | IptvSeries,
    type: 'live' | 'movie' | 'series'
  ) => {
    if (!userId) return;
    const isChannel = 'streamUrl' in item;
    const logoOrPoster = ('logo' in item ? item.logo : (item as any).poster) || '';

    await toggleIptvFavorite({
      userId,
      itemId: item.id,
      type,
      name: item.name,
      category: item.category,
      poster: logoOrPoster,
      logo: logoOrPoster,
      streamUrl: isChannel ? (item as any).streamUrl : undefined,
      sourceId: item.sourceId,
      addedAt: new Date().toISOString()
    }, userId);
  };

  const isItemFavorite = (itemId: string) => {
    return favorites.some(f => f.itemId === itemId);
  };

  // Open series modal and load detailed seasons/episodes if Xtream
  const handleOpenSeries = async (series: IptvSeries) => {
    setSelectedSeriesDetail(series);

    if ((!series.seasons || series.seasons.length === 0) && series.seriesId) {
      const parentSource = sources.find(s => s.id === series.sourceId);
      if (parentSource && parentSource.type === 'xtream') {
        setLoadingSeriesDetail(true);
        const detailed = await fetchXtreamSeriesInfo(parentSource, series.seriesId);
        if (detailed && detailed.seasons) {
          setSelectedSeriesDetail(detailed);
        }
        setLoadingSeriesDetail(false);
      }
    }
  };

  // Currently selected source object
  const selectedSource = useMemo(() => {
    return sources.find(s => s.id === selectedSourceId) || sources[0] || null;
  }, [sources, selectedSourceId]);

  // Keep selectedSourceId in sync if sources change and none is selected
  useEffect(() => {
    if (sources.length > 0 && (!selectedSourceId || !sources.some(s => s.id === selectedSourceId))) {
      setSelectedSourceId(sources[0].id);
    }
  }, [sources, selectedSourceId]);

  // Extract unique categories for current tab and selected source
  const categoriesList = useMemo(() => {
    if (!selectedSourceId) return [];
    const set = new Set<string>();

    if (activeTab === 'live') {
      channels
        .filter(c => c.sourceId === selectedSourceId)
        .forEach(c => { if (c.category) set.add(c.category); });
    } else if (activeTab === 'movies') {
      movies
        .filter(m => m.sourceId === selectedSourceId)
        .forEach(m => { if (m.category) set.add(m.category); });
    } else if (activeTab === 'series') {
      seriesList
        .filter(s => s.sourceId === selectedSourceId)
        .forEach(s => { if (s.category) set.add(s.category); });
    }

    return Array.from(set).sort();
  }, [activeTab, channels, movies, seriesList, selectedSourceId]);

  // Filtered Channels
  const filteredChannels = useMemo(() => {
    if (!selectedSourceId) return [];
    const q = deferredSearchQuery.toLowerCase().trim();
    return channels.filter(c => {
      const matchSource = c.sourceId === selectedSourceId;
      const matchCat = selectedCategory === 'all' || c.category === selectedCategory;
      const matchSearch = !q || c.name.toLowerCase().includes(q) || (c.category && c.category.toLowerCase().includes(q));
      return matchSource && matchCat && matchSearch;
    });
  }, [channels, selectedSourceId, selectedCategory, deferredSearchQuery]);

  // Filtered Movies
  const filteredMovies = useMemo(() => {
    if (!selectedSourceId) return [];
    const q = deferredSearchQuery.toLowerCase().trim();
    return movies.filter(m => {
      const matchSource = m.sourceId === selectedSourceId;
      const matchCat = selectedCategory === 'all' || m.category === selectedCategory;
      const matchSearch = !q || m.name.toLowerCase().includes(q) || (m.category && m.category.toLowerCase().includes(q));
      return matchSource && matchCat && matchSearch;
    });
  }, [movies, selectedSourceId, selectedCategory, deferredSearchQuery]);

  // Filtered Series
  const filteredSeries = useMemo(() => {
    if (!selectedSourceId) return [];
    const q = deferredSearchQuery.toLowerCase().trim();
    return seriesList.filter(s => {
      const matchSource = s.sourceId === selectedSourceId;
      const matchCat = selectedCategory === 'all' || s.category === selectedCategory;
      const matchSearch = !q || s.name.toLowerCase().includes(q) || (s.category && s.category.toLowerCase().includes(q));
      return matchSource && matchCat && matchSearch;
    });
  }, [seriesList, selectedSourceId, selectedCategory, deferredSearchQuery]);

  // Filtered Favorites
  const filteredFavorites = useMemo(() => {
    const q = deferredSearchQuery.toLowerCase().trim();
    return favorites.filter(f => {
      return !q || f.name.toLowerCase().includes(q) || (f.category && f.category.toLowerCase().includes(q));
    });
  }, [favorites, deferredSearchQuery]);

  // Counts for current source
  const currentSourceChannelsCount = useMemo(() => {
    if (!selectedSourceId) return 0;
    return channels.filter(c => c.sourceId === selectedSourceId).length;
  }, [channels, selectedSourceId]);

  const currentSourceMoviesCount = useMemo(() => {
    if (!selectedSourceId) return 0;
    return movies.filter(m => m.sourceId === selectedSourceId).length;
  }, [movies, selectedSourceId]);

  const currentSourceSeriesCount = useMemo(() => {
    if (!selectedSourceId) return 0;
    return seriesList.filter(s => s.sourceId === selectedSourceId).length;
  }, [seriesList, selectedSourceId]);

  // Visible Slices for 60fps rendering
  const displayedChannels = useMemo(() => {
    return filteredChannels.slice(0, visibleCount);
  }, [filteredChannels, visibleCount]);

  const displayedMovies = useMemo(() => {
    return filteredMovies.slice(0, visibleCount);
  }, [filteredMovies, visibleCount]);

  const displayedSeries = useMemo(() => {
    return filteredSeries.slice(0, visibleCount);
  }, [filteredSeries, visibleCount]);

  // Empty state prompt when no list is selected
  const renderSelectSourcePrompt = (typeLabel: string) => {
    if (sources.length === 0) {
      return (
        <div className="py-16 px-6 text-center bg-slate-900/40 rounded-3xl border border-slate-800/80 max-w-xl mx-auto my-6 space-y-4 shadow-xl">
          <div className="w-16 h-16 rounded-2xl bg-red-600/10 border border-red-500/20 text-red-400 flex items-center justify-center mx-auto shadow-inner">
            <Server className="w-8 h-8" />
          </div>
          <div className="space-y-1.5">
            <h3 className="text-lg font-bold text-white">Cap llista IPTV configurada</h3>
            <p className="text-xs sm:text-sm text-slate-400 max-w-md mx-auto">
              Afegeix una llista M3U o Xtream Codes des de la pestanya &quot;Llistes IPTV&quot; per començar a veure canals.
            </p>
          </div>
          <button
            id="iptv-manage-sources-prompt-btn"
            data-focusable="true"
            onClick={() => setActiveTab('sources')}
            className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl transition-all shadow-md active:scale-95 outline-none focus:ring-2 focus:ring-red-400"
          >
            Gestionar Llistes IPTV
          </button>
        </div>
      );
    }

    return (
      <div className="py-16 px-6 text-center bg-slate-900/40 rounded-3xl border border-slate-800/80 max-w-xl mx-auto my-6 space-y-5 shadow-xl">
        <div className="w-16 h-16 rounded-2xl bg-red-600/10 border border-red-500/20 text-red-400 flex items-center justify-center mx-auto shadow-inner">
          <List className="w-8 h-8" />
        </div>
        <div className="space-y-1.5">
          <h3 className="text-lg font-bold text-white">Selecciona una llista IPTV</h3>
          <p className="text-xs sm:text-sm text-slate-400 max-w-md mx-auto">
            Tria una de les teves llistes IPTV al desplegable superior per carregar els {typeLabel} de manera ràpida i optimitzada.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
          {sources.map((s, sIdx) => (
            <button
              key={s.id}
              id={`iptv-prompt-source-${sIdx}`}
              data-focusable="true"
              onClick={() => {
                setSelectedSourceId(s.id);
                setSelectedCategory('all');
              }}
              className="flex items-center gap-2 px-3.5 py-2 bg-slate-800/90 hover:bg-red-600 hover:text-white text-slate-300 text-xs font-semibold rounded-xl border border-slate-700 hover:border-red-500 transition-all shadow-sm outline-none focus:ring-2 focus:ring-red-500 focus:bg-red-600 focus:text-white"
            >
              <Tv className="w-3.5 h-3.5 text-red-400 group-hover:text-white" />
              <span>{s.name}</span>
              <span className="text-[10px] opacity-75 font-mono px-1.5 py-0.5 bg-black/40 rounded">
                {s.type === 'xtream' ? 'Xtream' : 'M3U'}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen pb-24 text-slate-100 font-sans">
      {/* Top Banner / Header */}
      <div className="bg-gradient-to-b from-red-950/40 via-slate-900/40 to-transparent p-4 sm:p-6 border-b border-slate-800/80">
        <div className="max-w-7xl mx-auto space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-gradient-to-br from-red-600 to-red-700 text-white shadow-lg shadow-red-600/30">
              <Tv className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                IPTV
              </h1>
              <p className="text-xs text-slate-400 hidden sm:block">
                Televisió en directe, pel·lícules i sèries VOD
              </p>
            </div>
          </div>

          {/* Sync Progress notification */}
          {syncProgress && (
            <div className="p-3 bg-slate-900 border border-red-500/30 rounded-xl space-y-1.5 animate-in slide-in-from-top">
              <div className="flex justify-between text-xs font-medium text-slate-300">
                <span className="flex items-center gap-2 text-red-400">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  {syncProgress.message}
                </span>
                <span>{syncProgress.progressPercent}%</span>
              </div>
              <div className="w-full bg-slate-800 rounded-full h-1.5 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-red-600 to-amber-500 h-full transition-all duration-300"
                  style={{ width: `${syncProgress.progressPercent}%` }}
                />
              </div>
            </div>
          )}

          {/* Top Level Nav Tabs (TV Directe, Pel·lícules, Sèries, Preferits, Llistes) */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
            <button
              id="tab-iptv-live"
              data-focusable="true"
              onClick={() => { setActiveTab('live'); setSelectedCategory('all'); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm whitespace-nowrap transition-all outline-none focus:ring-2 focus:ring-red-500 ${
                activeTab === 'live'
                  ? 'bg-red-600 text-white shadow-lg shadow-red-600/30 ring-1 ring-red-400'
                  : 'bg-slate-900/80 text-slate-300 hover:bg-slate-800 hover:text-white border border-slate-800'
              }`}
            >
              <Radio className="w-4 h-4" />
              TV en Directe
              {selectedSourceId && (
                <span className="px-1.5 py-0.5 rounded-md bg-black/30 text-[10px] font-mono">
                  {currentSourceChannelsCount}
                </span>
              )}
            </button>

            <button
              id="tab-iptv-movies"
              data-focusable="true"
              onClick={() => { setActiveTab('movies'); setSelectedCategory('all'); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm whitespace-nowrap transition-all outline-none focus:ring-2 focus:ring-red-500 ${
                activeTab === 'movies'
                  ? 'bg-red-600 text-white shadow-lg shadow-red-600/30 ring-1 ring-red-400'
                  : 'bg-slate-900/80 text-slate-300 hover:bg-slate-800 hover:text-white border border-slate-800'
              }`}
            >
              <Film className="w-4 h-4" />
              Pel·lícules
              {selectedSourceId && (
                <span className="px-1.5 py-0.5 rounded-md bg-black/30 text-[10px] font-mono">
                  {currentSourceMoviesCount}
                </span>
              )}
            </button>

            <button
              id="tab-iptv-series"
              data-focusable="true"
              onClick={() => { setActiveTab('series'); setSelectedCategory('all'); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm whitespace-nowrap transition-all outline-none focus:ring-2 focus:ring-red-500 ${
                activeTab === 'series'
                  ? 'bg-red-600 text-white shadow-lg shadow-red-600/30 ring-1 ring-red-400'
                  : 'bg-slate-900/80 text-slate-300 hover:bg-slate-800 hover:text-white border border-slate-800'
              }`}
            >
              <Layers className="w-4 h-4" />
              Sèries
              {selectedSourceId && (
                <span className="px-1.5 py-0.5 rounded-md bg-black/30 text-[10px] font-mono">
                  {currentSourceSeriesCount}
                </span>
              )}
            </button>

            <button
              id="tab-iptv-favorites"
              data-focusable="true"
              onClick={() => { setActiveTab('favorites'); setSelectedCategory('all'); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm whitespace-nowrap transition-all outline-none focus:ring-2 focus:ring-amber-500 ${
                activeTab === 'favorites'
                  ? 'bg-amber-600 text-white shadow-lg shadow-amber-600/30 ring-1 ring-amber-400'
                  : 'bg-slate-900/80 text-slate-300 hover:bg-slate-800 hover:text-white border border-slate-800'
              }`}
            >
              <Star className="w-4 h-4 text-amber-300 fill-current" />
              Preferits
              <span className="px-1.5 py-0.5 rounded-md bg-black/30 text-[10px] font-mono">
                {favorites.length}
              </span>
            </button>

            <button
              id="tab-iptv-sources"
              data-focusable="true"
              onClick={() => { setActiveTab('sources'); setSelectedCategory('all'); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm whitespace-nowrap transition-all outline-none focus:ring-2 focus:ring-slate-400 ${
                activeTab === 'sources'
                  ? 'bg-slate-700 text-white shadow-lg shadow-slate-700/30 ring-1 ring-slate-400'
                  : 'bg-slate-900/80 text-slate-300 hover:bg-slate-800 hover:text-white border border-slate-800'
              }`}
            >
              <Server className="w-4 h-4 text-red-400" />
              Llistes IPTV
              <span className="px-1.5 py-0.5 rounded-md bg-black/30 text-[10px] font-mono">
                {sources.length}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
        {activeTab !== 'sources' && (
          <>
            {/* Search & Filter Controls: Llistes i Categories desplegables junts */}
            <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
              {/* Search Input */}
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  id="iptv-search-input"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={
                    activeTab === 'live' 
                      ? 'Cercar canals (nom o categoria)...' 
                      : activeTab === 'movies' 
                      ? 'Cercar pel·lícules...' 
                      : activeTab === 'series' 
                      ? 'Cercar sèries...' 
                      : 'Cercar a preferits...'
                  }
                  className="w-full pl-10 pr-9 py-2.5 bg-slate-900/90 border border-slate-800 rounded-xl text-white text-xs sm:text-sm focus:outline-none focus:border-red-500 transition-colors shadow-inner"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Selector de Llistes i Categoria Junts (Accessible per TV i Comandament) */}
              {activeTab !== 'favorites' && (
                <div className="flex flex-wrap sm:flex-nowrap items-center gap-2.5">
                  {/* 1. Selector de Llista Personalitzat */}
                  <div ref={sourceDropdownRef} className="relative flex-1 sm:flex-initial">
                    <button
                      id="iptv-source-selector-btn"
                      data-focusable="true"
                      type="button"
                      onClick={() => {
                        setIsSourceDropdownOpen(prev => !prev);
                        setIsCategoryDropdownOpen(false);
                      }}
                      className={`flex items-center gap-2 px-3 py-2 bg-slate-900/90 hover:bg-slate-800 border rounded-xl shadow-inner w-full sm:w-auto text-left transition-all outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                        isSourceDropdownOpen ? 'border-red-500 ring-2 ring-red-500/50' : 'border-slate-800'
                      }`}
                    >
                      <List className="w-3.5 h-3.5 text-red-400 shrink-0" />
                      <span className="text-xs text-slate-400 font-medium whitespace-nowrap">Llista:</span>
                      <span className="text-xs font-semibold text-white truncate max-w-[160px] sm:max-w-[200px]">
                        {selectedSource ? `${selectedSource.name} (${selectedSource.type === 'xtream' ? 'Xtream' : 'M3U'})` : '-- Selecciona una llista --'}
                      </span>
                      <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ml-auto shrink-0 ${isSourceDropdownOpen ? 'rotate-180 text-red-400' : ''}`} />
                    </button>

                    {/* Llista Dropdown Menu */}
                    {isSourceDropdownOpen && (
                      <div
                        id="iptv-source-dropdown-menu"
                        className="absolute left-0 top-full mt-1.5 w-72 max-h-64 overflow-y-auto bg-slate-900/95 border border-slate-700/80 rounded-xl shadow-2xl p-1.5 z-40 backdrop-blur-md animate-in fade-in"
                      >
                        <div className="text-[10px] uppercase font-bold text-slate-400 px-2 py-1">
                          Llistes Disponibles ({sources.length})
                        </div>
                        {sources.length === 0 ? (
                          <div className="px-3 py-2 text-xs text-slate-400">
                            Cap llista configurada. Afegeix-ne una a la pestanya &quot;Llistes IPTV&quot;.
                          </div>
                        ) : (
                          sources.map((s, sIdx) => {
                            const isSelected = selectedSourceId === s.id;
                            return (
                              <button
                                key={s.id}
                                id={`iptv-source-option-${sIdx}`}
                                data-focusable="true"
                                type="button"
                                onClick={() => {
                                  setSelectedSourceId(s.id);
                                  setSelectedCategory('all');
                                  setIsSourceDropdownOpen(false);
                                }}
                                className={`w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-xs font-semibold text-left transition-all outline-none focus:ring-2 focus:ring-red-500 focus:bg-red-600/20 ${
                                  isSelected ? 'bg-red-600 text-white shadow-md' : 'text-slate-200 hover:bg-slate-800'
                                }`}
                              >
                                <span className="truncate">{s.name}</span>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                                    isSelected ? 'bg-black/30 text-white' : 'bg-slate-800 text-slate-400'
                                  }`}>
                                    {s.type === 'xtream' ? 'Xtream' : 'M3U'}
                                  </span>
                                  {isSelected && <Check className="w-3.5 h-3.5 text-white shrink-0" />}
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>

                  {/* 2. Selector de Categoria Personalitzat */}
                  <div ref={categoryDropdownRef} className="relative flex-1 sm:flex-initial">
                    <button
                      id="iptv-category-selector-btn"
                      data-focusable="true"
                      type="button"
                      disabled={!selectedSourceId}
                      onClick={() => {
                        if (!selectedSourceId) return;
                        setIsCategoryDropdownOpen(prev => !prev);
                        setIsSourceDropdownOpen(false);
                      }}
                      className={`flex items-center gap-2 px-3 py-2 bg-slate-900/90 border rounded-xl shadow-inner w-full sm:w-auto text-left transition-all outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                        !selectedSourceId
                          ? 'opacity-50 border-slate-800/50 cursor-not-allowed'
                          : isCategoryDropdownOpen
                            ? 'border-red-500 ring-2 ring-red-500/50 hover:bg-slate-800'
                            : 'border-slate-800 hover:bg-slate-800'
                      }`}
                    >
                      <Filter className="w-3.5 h-3.5 text-red-400 shrink-0" />
                      <span className="text-xs text-slate-400 font-medium whitespace-nowrap">Categoria:</span>
                      <span className="text-xs font-semibold text-white truncate max-w-[150px] sm:max-w-[190px]">
                        {selectedCategory === 'all'
                          ? categoriesList.length > 0 ? `Totes (${categoriesList.length})` : 'Totes'
                          : selectedCategory}
                      </span>
                      <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ml-auto shrink-0 ${isCategoryDropdownOpen ? 'rotate-180 text-red-400' : ''}`} />
                    </button>

                    {/* Categoria Dropdown Menu */}
                    {isCategoryDropdownOpen && (
                      <div
                        id="iptv-category-dropdown-menu"
                        className="absolute left-0 top-full mt-1.5 w-72 max-h-72 overflow-y-auto bg-slate-900/95 border border-slate-700/80 rounded-xl shadow-2xl p-1.5 z-40 backdrop-blur-md animate-in fade-in"
                      >
                        <div className="text-[10px] uppercase font-bold text-slate-400 px-2 py-1">
                          Categories ({categoriesList.length})
                        </div>
                        {/* Option: Totes les categories */}
                        <button
                          id="iptv-category-option-all"
                          data-focusable="true"
                          type="button"
                          onClick={() => {
                            setSelectedCategory('all');
                            setIsCategoryDropdownOpen(false);
                          }}
                          className={`w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-xs font-semibold text-left transition-all outline-none focus:ring-2 focus:ring-red-500 focus:bg-red-600/20 ${
                            selectedCategory === 'all' ? 'bg-red-600 text-white shadow-md' : 'text-slate-200 hover:bg-slate-800'
                          }`}
                        >
                          <span>Totes les categories</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                            selectedCategory === 'all' ? 'bg-black/30 text-white' : 'bg-slate-800 text-slate-400'
                          }`}>
                            {categoriesList.length}
                          </span>
                        </button>

                        {categoriesList.map((cat, cIdx) => {
                          const isSelected = selectedCategory === cat;
                          return (
                            <button
                              key={cat}
                              id={`iptv-category-option-${cIdx}`}
                              data-focusable="true"
                              type="button"
                              onClick={() => {
                                setSelectedCategory(cat);
                                setIsCategoryDropdownOpen(false);
                              }}
                              className={`w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-xs font-semibold text-left transition-all outline-none focus:ring-2 focus:ring-red-500 focus:bg-red-600/20 ${
                                isSelected ? 'bg-red-600 text-white shadow-md' : 'text-slate-200 hover:bg-slate-800'
                              }`}
                            >
                              <span className="truncate">{cat}</span>
                              {isSelected && <Check className="w-3.5 h-3.5 text-white shrink-0" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* 1. LIVE TV TAB */}
        {activeTab === 'live' && (
          <div className="space-y-4">
            {!selectedSourceId ? (
              renderSelectSourcePrompt('canals de televisió en directe')
            ) : filteredChannels.length > 0 ? (
              <>
                <div className="flex items-center justify-between text-xs text-slate-400 px-1">
                  <span>Mostrant {displayedChannels.length} de {filteredChannels.length} canals</span>
                  {deferredSearchQuery && <span>Cerca: &quot;{deferredSearchQuery}&quot;</span>}
                </div>
                <div 
                  data-grid="true"
                  data-columns="5"
                  className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-5 xl:grid-cols-5 gap-3 sm:gap-4"
                >
                  {displayedChannels.map((ch, idx) => {
                    const isFav = isItemFavorite(ch.id);
                    return (
                      <div
                        key={ch.id}
                        id={`iptv-ch-${ch.id}`}
                        data-focusable="true"
                        onClick={() => setActivePlayingItem(ch)}
                        className="group bg-slate-900/80 hover:bg-slate-850 border border-slate-800/90 hover:border-red-500/50 rounded-2xl p-3.5 flex flex-col justify-between cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-md hover:shadow-red-600/10 relative focus:outline-none focus:ring-2 focus:ring-red-500"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') setActivePlayingItem(ch);
                        }}
                      >
                        {/* Top Bar: Channel Number & Favorite button */}
                        <div className="flex items-center justify-between gap-1 mb-2">
                          <span className="text-[11px] font-bold font-mono px-2 py-0.5 bg-slate-950/80 text-slate-400 rounded-md border border-slate-800">
                            {ch.number || idx + 1}
                          </span>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleFav(ch, 'live');
                            }}
                            className={`p-1 rounded-lg hover:bg-slate-800 transition-colors ${
                              isFav ? 'text-amber-400' : 'text-slate-500 hover:text-amber-400'
                            }`}
                            title="Afegir a preferits"
                          >
                            <Star className="w-3.5 h-3.5" fill={isFav ? 'currentColor' : 'none'} />
                          </button>
                        </div>

                        {/* Channel Logo */}
                        <div className="h-20 flex items-center justify-center p-2 bg-slate-950/50 rounded-xl my-1 relative overflow-hidden group-hover:bg-slate-950 transition-colors">
                          <ChannelLogo
                            logo={ch.logo}
                            name={ch.name}
                            category={ch.category}
                            userAgent={ch.userAgent}
                            size="md"
                            className="w-full h-full"
                          />

                          <div className="absolute inset-0 bg-red-600/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <div className="p-2 rounded-full bg-red-600 text-white shadow-lg">
                              <Play className="w-4 h-4 fill-current ml-0.5" />
                            </div>
                          </div>
                        </div>

                        {/* Channel Info */}
                        <div className="mt-2 text-left">
                          <MarqueeText
                            text={ch.name}
                            className="font-bold text-xs sm:text-sm text-white"
                            activeClassName="group-hover:text-red-400"
                          />
                          <div className="text-[11px] text-slate-400 truncate mt-0.5">
                            {ch.category || 'General'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {displayedChannels.length < filteredChannels.length && (
                  <div ref={loadMoreRef} className="py-6 text-center text-slate-400 text-xs flex items-center justify-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin text-red-400" />
                    Carregant més canals ({displayedChannels.length} de {filteredChannels.length})...
                  </div>
                )}
              </>
            ) : (
              <div className="py-16 text-center bg-slate-900/30 rounded-2xl border border-slate-800 text-slate-400 space-y-3">
                <Tv className="w-12 h-12 mx-auto text-slate-600" />
                <p className="text-base font-semibold text-slate-300">No s&apos;ha trobat cap canal de televisió.</p>
                <p className="text-xs text-slate-400">
                  {channels.length === 0 
                    ? 'Afegeix una font M3U o Xtream des de la pestanya "Llistes IPTV".' 
                    : 'Prova a canviar els filtres de cerca o categoria.'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* 2. MOVIES TAB */}
        {activeTab === 'movies' && (
          <div className="space-y-4">
            {!selectedSourceId ? (
              renderSelectSourcePrompt('pel·lícules VOD')
            ) : filteredMovies.length > 0 ? (
              <>
                <div className="flex items-center justify-between text-xs text-slate-400 px-1">
                  <span>Mostrant {displayedMovies.length} de {filteredMovies.length} pel·lícules</span>
                </div>
                <div 
                  data-grid="true"
                  data-columns="5"
                  className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-5 xl:grid-cols-5 gap-4"
                >
                  {displayedMovies.map((movie) => {
                    const isFav = isItemFavorite(movie.id);
                    return (
                      <div
                        key={movie.id}
                        id={`iptv-movie-${movie.id}`}
                        data-focusable="true"
                        onClick={() => setActivePlayingItem(movie)}
                        className="group bg-slate-900 border border-slate-800 hover:border-red-500/50 rounded-2xl overflow-hidden cursor-pointer transition-all duration-200 hover:scale-[1.02] shadow-lg flex flex-col justify-between focus:outline-none focus:ring-2 focus:ring-red-500"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') setActivePlayingItem(movie);
                        }}
                      >
                        {/* Poster */}
                        <div className="aspect-[2/3] bg-slate-950 relative overflow-hidden">
                          {movie.poster ? (
                            <img
                              src={getProxiedImageUrl(movie.poster, movie.userAgent)}
                              alt=""
                              className="w-full h-full object-cover transition-transform group-hover:scale-105 duration-300"
                              loading="lazy"
                              onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center p-4 text-slate-600">
                              <Film className="w-10 h-10 mb-2" />
                              <span className="text-[11px] text-center text-slate-400">{movie.name}</span>
                            </div>
                          )}

                          <div className="absolute top-2 right-2 z-10">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleFav(movie, 'movie');
                              }}
                              className={`p-1.5 rounded-full bg-black/60 backdrop-blur-sm transition-colors ${
                                isFav ? 'text-amber-400' : 'text-white/70 hover:text-amber-400'
                              }`}
                            >
                              <Star className="w-3.5 h-3.5" fill={isFav ? 'currentColor' : 'none'} />
                            </button>
                          </div>

                          {movie.rating && (
                            <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded-md bg-amber-500 text-black font-extrabold text-[10px] shadow">
                              ★ {movie.rating.toFixed(1)}
                            </div>
                          )}

                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <div className="p-3 rounded-full bg-red-600 text-white shadow-xl transform scale-90 group-hover:scale-100 transition-transform">
                              <Play className="w-5 h-5 fill-current ml-0.5" />
                            </div>
                          </div>
                        </div>

                        {/* Metadata */}
                        <div className="p-3">
                          <MarqueeText
                            text={movie.name}
                            className="font-bold text-xs sm:text-sm text-white"
                            activeClassName="group-hover:text-red-400"
                          />
                          <div className="flex items-center justify-between text-[11px] text-slate-400 mt-1">
                            <span className="truncate">{movie.category || 'Pel·lícules'}</span>
                            {movie.year && <span>{movie.year}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {displayedMovies.length < filteredMovies.length && (
                  <div ref={loadMoreRef} className="py-6 text-center text-slate-400 text-xs flex items-center justify-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin text-red-400" />
                    Carregant més pel·lícules...
                  </div>
                )}
              </>
            ) : (
              <div className="py-16 text-center bg-slate-900/30 rounded-2xl border border-slate-800 text-slate-400 space-y-3">
                <Film className="w-12 h-12 mx-auto text-slate-600" />
                <p className="text-base font-semibold text-slate-300">No s&apos;han trobat pel·lícules VOD.</p>
                <p className="text-xs text-slate-400">Comprova que la font IPTV inclogui contingut VOD i estigui sincronitzada.</p>
              </div>
            )}
          </div>
        )}

        {/* 3. SERIES TAB */}
        {activeTab === 'series' && (
          <div className="space-y-4">
            {!selectedSourceId ? (
              renderSelectSourcePrompt('sèries VOD')
            ) : filteredSeries.length > 0 ? (
              <>
                <div className="flex items-center justify-between text-xs text-slate-400 px-1">
                  <span>Mostrant {displayedSeries.length} de {filteredSeries.length} sèries</span>
                </div>
                <div 
                  data-grid="true"
                  data-columns="5"
                  className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-5 xl:grid-cols-5 gap-4"
                >
                  {displayedSeries.map((series) => {
                    const isFav = isItemFavorite(series.id);
                    return (
                      <div
                        key={series.id}
                        id={`iptv-series-${series.id}`}
                        data-focusable="true"
                        onClick={() => handleOpenSeries(series)}
                        className="group bg-slate-900 border border-slate-800 hover:border-red-500/50 rounded-2xl overflow-hidden cursor-pointer transition-all duration-200 hover:scale-[1.02] shadow-lg flex flex-col justify-between focus:outline-none focus:ring-2 focus:ring-red-500"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleOpenSeries(series);
                        }}
                      >
                        {/* Poster */}
                        <div className="aspect-[2/3] bg-slate-950 relative overflow-hidden">
                          {series.poster ? (
                            <img
                              src={getProxiedImageUrl(series.poster, series.userAgent)}
                              alt=""
                              className="w-full h-full object-cover transition-transform group-hover:scale-105 duration-300"
                              loading="lazy"
                              onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center p-4 text-slate-600">
                              <Layers className="w-10 h-10 mb-2" />
                              <span className="text-[11px] text-center text-slate-400">{series.name}</span>
                            </div>
                          )}

                          <div className="absolute top-2 right-2 z-10">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleFav(series, 'series');
                              }}
                              className={`p-1.5 rounded-full bg-black/60 backdrop-blur-sm transition-colors ${
                                isFav ? 'text-amber-400' : 'text-white/70 hover:text-amber-400'
                              }`}
                            >
                              <Star className="w-3.5 h-3.5" fill={isFav ? 'currentColor' : 'none'} />
                            </button>
                          </div>

                          {series.rating && (
                            <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded-md bg-amber-500 text-black font-extrabold text-[10px] shadow">
                              ★ {series.rating.toFixed(1)}
                            </div>
                          )}

                          <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-black/80 backdrop-blur-sm text-[10px] font-semibold text-slate-300">
                            {series.seasonsCount ? `${series.seasonsCount} Temp.` : 'Sèrie'}
                          </div>
                        </div>

                        {/* Metadata */}
                        <div className="p-3">
                          <MarqueeText
                            text={series.name}
                            className="font-bold text-xs sm:text-sm text-white"
                            activeClassName="group-hover:text-red-400"
                          />
                          <div className="flex items-center justify-between text-[11px] text-slate-400 mt-1">
                            <span className="truncate">{series.category || 'Sèries'}</span>
                            {series.year && <span>{series.year}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {displayedSeries.length < filteredSeries.length && (
                  <div ref={loadMoreRef} className="py-6 text-center text-slate-400 text-xs flex items-center justify-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin text-red-400" />
                    Carregant més sèries...
                  </div>
                )}
              </>
            ) : (
              <div className="py-16 text-center bg-slate-900/30 rounded-2xl border border-slate-800 text-slate-400 space-y-3">
                <Layers className="w-12 h-12 mx-auto text-slate-600" />
                <p className="text-base font-semibold text-slate-300">No s&apos;han trobat sèries VOD.</p>
                <p className="text-xs text-slate-400">Comprova les llistes IPTV configurades al panell d&apos;administració.</p>
              </div>
            )}
          </div>
        )}

        {/* 4. FAVORITES TAB */}
        {activeTab === 'favorites' && (
          <div className="space-y-4">
            {filteredFavorites.length > 0 ? (
              <div 
                data-grid="true"
                data-columns="5"
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-5 xl:grid-cols-5 gap-4"
              >
                {filteredFavorites.map((fav) => {
                  return (
                    <div
                      key={fav.id}
                      id={`iptv-fav-${fav.id}`}
                      data-focusable="true"
                      tabIndex={0}
                      onClick={() => {
                        if (fav.type === 'live' || fav.type === 'movie') {
                          setActivePlayingItem({
                            id: fav.itemId,
                            name: fav.name,
                            streamUrl: fav.streamUrl || '',
                            logo: fav.logo || fav.poster,
                            category: fav.category || 'Preferits',
                            sourceId: fav.sourceId
                          });
                        } else {
                          const originalSeries = seriesList.find(s => s.id === fav.itemId);
                          if (originalSeries) {
                            handleOpenSeries(originalSeries);
                          }
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          if (fav.type === 'live' || fav.type === 'movie') {
                            setActivePlayingItem({
                              id: fav.itemId,
                              name: fav.name,
                              streamUrl: fav.streamUrl || '',
                              logo: fav.logo || fav.poster,
                              category: fav.category || 'Preferits',
                              sourceId: fav.sourceId
                            });
                          } else {
                            const originalSeries = seriesList.find(s => s.id === fav.itemId);
                            if (originalSeries) {
                              handleOpenSeries(originalSeries);
                            }
                          }
                        }
                      }}
                      className="group bg-slate-900 border border-slate-800 hover:border-amber-500/50 rounded-2xl p-3 flex flex-col justify-between cursor-pointer transition-all hover:scale-[1.02] shadow-lg relative focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 uppercase">
                          {fav.type === 'live' ? 'Canal' : fav.type === 'movie' ? 'Pel·lícula' : 'Sèrie'}
                        </span>
                        <Star className="w-4 h-4 text-amber-400 fill-current" />
                      </div>

                      <div className="h-24 flex items-center justify-center bg-slate-950/60 rounded-xl my-1 p-2">
                        <ChannelLogo
                          logo={fav.poster || fav.logo}
                          name={fav.name}
                          category={fav.category}
                          type={fav.type === 'movie' ? 'movie' : fav.type === 'series' ? 'series' : 'live'}
                          size="md"
                          className="w-full h-full"
                        />
                      </div>

                      <div className="mt-2 text-left">
                        <MarqueeText
                          text={fav.name}
                          className="font-bold text-xs sm:text-sm text-white"
                        />
                        <div className="text-[10px] text-slate-400 truncate">{fav.category}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-16 text-center bg-slate-900/30 rounded-2xl border border-slate-800 text-slate-400 space-y-3">
                <Star className="w-12 h-12 mx-auto text-amber-400/50" />
                <p className="text-base font-semibold text-slate-300">No tens cap canal o títol a preferits.</p>
                <p className="text-xs text-slate-400">Fes clic a la icona de l&apos;estrella a qualsevol canal o pel·lícula per afegir-lo aquí.</p>
              </div>
            )}
          </div>
        )}

        {/* 5. SOURCES MANAGEMENT TAB (With Edit & Delete Inside Card) */}
        {activeTab === 'sources' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-900/60 border border-slate-800 p-4 rounded-2xl">
              <div>
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Server className="w-5 h-5 text-red-500" />
                  Gestió de Llistes IPTV (M3U & Xtream)
                </h2>
                <p className="text-xs text-slate-400 mt-0.5">
                  Afegeix, edita, elimina o sincronitza les teves llistes de reproducció IPTV.
                </p>
              </div>

              <button
                id="btn-add-iptv-source"
                onClick={() => {
                  setEditingSource({
                    name: '',
                    type: 'm3u',
                    url: '',
                    serverUrl: '',
                    username: '',
                    password: '',
                    userAgent: 'IPTVSmartersPro',
                    enabled: true
                  });
                  setTestResult(null);
                  setShowSourceModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 active:scale-95 text-white text-xs sm:text-sm font-bold rounded-xl shadow-lg shadow-red-600/30 transition-all"
              >
                <Plus className="w-4 h-4" />
                Afegir Nova Llista
              </button>
            </div>

            {sources.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sources.map((source) => {
                  const isSyncingThis = syncingSourceId === source.id;
                  return (
                    <div
                      key={source.id}
                      id={`iptv-source-card-${source.id}`}
                      className="bg-slate-900/90 border border-slate-800 hover:border-slate-700 rounded-2xl p-5 space-y-4 shadow-xl flex flex-col justify-between"
                    >
                      {/* Source Header */}
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className={`p-2.5 rounded-xl text-white ${
                              source.type === 'xtream' ? 'bg-indigo-600' : 'bg-red-600'
                            }`}>
                              {source.type === 'xtream' ? <Server className="w-5 h-5" /> : <List className="w-5 h-5" />}
                            </div>
                            <div>
                              <h3 className="text-base font-bold text-white">{source.name}</h3>
                              <div className="flex items-center gap-2 text-xs text-slate-400">
                                <span className="uppercase font-mono font-semibold px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">
                                  {source.type.toUpperCase()}
                                </span>
                                <span>•</span>
                                <span>{source.enabled ? 'Activa' : 'Desactivada'}</span>
                              </div>
                            </div>
                          </div>

                          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                            source.status === 'error'
                              ? 'bg-red-950 text-red-300 border border-red-800/80'
                              : 'bg-emerald-950 text-emerald-300 border border-emerald-800/80'
                          }`}>
                            {source.status === 'error' ? 'Error de Connexió' : 'Connectat'}
                          </span>
                        </div>

                        {/* URL / Server details */}
                        <div className="text-xs font-mono text-slate-400 bg-slate-950/60 p-2.5 rounded-xl border border-slate-800/60 truncate">
                          {source.type === 'm3u' ? (
                            <span>{source.url || 'Sense URL'}</span>
                          ) : (
                            <span>{source.serverUrl || 'Sense Servidor'} (Usuari: {source.username})</span>
                          )}
                        </div>

                        {/* Error info if present */}
                        {source.lastError && (
                          <div className="flex items-start gap-2 text-xs text-red-400 bg-red-950/40 border border-red-800/50 p-2.5 rounded-xl">
                            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <span className="font-semibold">Incidència: </span>
                              {source.lastError}
                            </div>
                          </div>
                        )}

                        {/* Stats Counts */}
                        <div className="grid grid-cols-3 gap-2 pt-1 text-center text-xs">
                          <div className="p-2 bg-slate-950/40 rounded-xl border border-slate-800/60">
                            <div className="text-slate-400 text-[10px]">Canals</div>
                            <div className="font-bold text-white text-sm">{source.liveCount || 0}</div>
                          </div>
                          <div className="p-2 bg-slate-950/40 rounded-xl border border-slate-800/60">
                            <div className="text-slate-400 text-[10px]">Pel·lícules</div>
                            <div className="font-bold text-white text-sm">{source.moviesCount || 0}</div>
                          </div>
                          <div className="p-2 bg-slate-950/40 rounded-xl border border-slate-800/60">
                            <div className="text-slate-400 text-[10px]">Sèries</div>
                            <div className="font-bold text-white text-sm">{source.seriesCount || 0}</div>
                          </div>
                        </div>

                        {source.lastSyncedAt && (
                          <div className="text-[11px] text-slate-500 flex items-center gap-1.5 pt-1">
                            <Clock className="w-3.5 h-3.5" />
                            Darrera sincronització: {new Date(source.lastSyncedAt).toLocaleString()}
                          </div>
                        )}
                      </div>

                      {/* Card Action Buttons: Edit, Delete, Sync */}
                      <div className="flex items-center gap-2 pt-3 border-t border-slate-800/80">
                        <button
                          id={`btn-sync-source-${source.id}`}
                          onClick={() => handleSyncSingleSource(source)}
                          disabled={isSyncingThis}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-800 hover:bg-slate-700 active:scale-95 text-slate-200 text-xs font-semibold rounded-xl border border-slate-700 transition-all disabled:opacity-50"
                        >
                          <RefreshCw className={`w-3.5 h-3.5 text-red-400 ${isSyncingThis ? 'animate-spin' : ''}`} />
                          {isSyncingThis ? 'Sincronitzant...' : 'Sincronitza'}
                        </button>

                        <button
                          id={`btn-edit-source-${source.id}`}
                          onClick={() => {
                            setEditingSource({ ...source });
                            setTestResult(null);
                            setShowSourceModal(true);
                          }}
                          className="flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-800 hover:bg-slate-700 active:scale-95 text-slate-200 text-xs font-semibold rounded-xl border border-slate-700 transition-all"
                          title="Editar Llista"
                        >
                          <Edit2 className="w-3.5 h-3.5 text-amber-400" />
                          <span>Editar</span>
                        </button>

                        <button
                          id={`btn-delete-source-${source.id}`}
                          onClick={() => handleDeleteSource(source.id, source.name)}
                          className="flex items-center justify-center gap-1.5 px-3 py-2 bg-red-950/40 hover:bg-red-900/60 active:scale-95 text-red-300 text-xs font-semibold rounded-xl border border-red-800/40 transition-all"
                          title="Eliminar Llista"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          <span>Eliminar</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-16 text-center bg-slate-900/30 rounded-2xl border border-slate-800 text-slate-400 space-y-4">
                <Server className="w-12 h-12 mx-auto text-slate-600" />
                <div className="space-y-1">
                  <p className="text-base font-semibold text-slate-300">No hi ha cap llista IPTV configurada.</p>
                  <p className="text-xs text-slate-400 max-w-md mx-auto">
                    Afegeix un enllaç M3U o credencials Xtream Codes per carregar automàticament els canals en directe i contingut VOD.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setEditingSource({
                      name: '',
                      type: 'm3u',
                      url: '',
                      serverUrl: '',
                      username: '',
                      password: '',
                      userAgent: 'IPTVSmartersPro',
                      enabled: true
                    });
                    setTestResult(null);
                    setShowSourceModal(true);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl shadow-lg transition-all"
                >
                  <Plus className="w-4 h-4" />
                  Afegir la primera llista
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add / Edit IPTV Source Modal */}
      {showSourceModal && editingSource && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-5 relative max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center pb-3 border-b border-slate-800">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Server className="w-5 h-5 text-red-500" />
                {editingSource.id ? 'Editar Llista IPTV' : 'Afegir Nova Llista IPTV'}
              </h3>
              <button
                onClick={() => {
                  setShowSourceModal(false);
                  setEditingSource(null);
                }}
                className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveSource} className="space-y-4">
              {/* Type selector */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-300">Tipus de Llista</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setEditingSource({ ...editingSource, type: 'm3u' })}
                    className={`p-3 rounded-xl border text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                      editingSource.type === 'm3u'
                        ? 'bg-red-600 text-white border-red-500 shadow-md'
                        : 'bg-slate-950 text-slate-400 border-slate-800 hover:bg-slate-800'
                    }`}
                  >
                    <List className="w-4 h-4" />
                    Enllaç M3U / M3U Plus
                  </button>

                  <button
                    type="button"
                    onClick={() => setEditingSource({ ...editingSource, type: 'xtream' })}
                    className={`p-3 rounded-xl border text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                      editingSource.type === 'xtream'
                        ? 'bg-indigo-600 text-white border-indigo-500 shadow-md'
                        : 'bg-slate-950 text-slate-400 border-slate-800 hover:bg-slate-800'
                    }`}
                  >
                    <Server className="w-4 h-4" />
                    Xtream Codes API
                  </button>
                </div>
              </div>

              {/* Source Name */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-300">Nom Identificatiu</label>
                <input
                  type="text"
                  required
                  placeholder="Ex: La Meva Llista IPTV"
                  value={editingSource.name || ''}
                  onChange={(e) => setEditingSource({ ...editingSource, name: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white text-xs sm:text-sm focus:outline-none focus:border-red-500"
                />
              </div>

              {/* M3U URL Fields */}
              {editingSource.type === 'm3u' ? (
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-300">URL de la llista M3U / M3U8</label>
                  <input
                    type="url"
                    required
                    placeholder="http://servidor.com/get.php?username=...&password=...&type=m3u_plus"
                    value={editingSource.url || ''}
                    onChange={(e) => setEditingSource({ ...editingSource, url: e.target.value })}
                    className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white text-xs sm:text-sm focus:outline-none focus:border-red-500 font-mono"
                  />
                  <p className="text-[11px] text-slate-400">
                    S&apos;admeten URLs directes de proveïdors o llistes M3U remotes.
                  </p>
                </div>
              ) : (
                /* Xtream Codes Fields */
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-300">URL del Servidor</label>
                    <input
                      type="url"
                      required
                      placeholder="http://servidor.com:8080"
                      value={editingSource.serverUrl || ''}
                      onChange={(e) => setEditingSource({ ...editingSource, serverUrl: e.target.value })}
                      className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white text-xs sm:text-sm focus:outline-none focus:border-indigo-500 font-mono"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-slate-300">Usuari</label>
                      <input
                        type="text"
                        required
                        placeholder="El teu usuari"
                        value={editingSource.username || ''}
                        onChange={(e) => setEditingSource({ ...editingSource, username: e.target.value })}
                        className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white text-xs sm:text-sm focus:outline-none focus:border-indigo-500 font-mono"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-slate-300">Contrasenya</label>
                      <input
                        type="password"
                        required
                        placeholder="••••••••"
                        value={editingSource.password || ''}
                        onChange={(e) => setEditingSource({ ...editingSource, password: e.target.value })}
                        className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white text-xs sm:text-sm focus:outline-none focus:border-indigo-500 font-mono"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* User Agent */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-300">User-Agent (Opcional)</label>
                <input
                  type="text"
                  placeholder="IPTVSmartersPro o VLC/3.0.18"
                  value={editingSource.userAgent || ''}
                  onChange={(e) => setEditingSource({ ...editingSource, userAgent: e.target.value })}
                  className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white text-xs focus:outline-none focus:border-red-500 font-mono"
                />
              </div>

              {/* Test Connection Results */}
              {testResult && (
                <div className={`p-3 rounded-xl border text-xs space-y-2 ${
                  testResult.success
                    ? 'bg-emerald-950/50 border-emerald-800/80 text-emerald-300'
                    : 'bg-red-950/50 border-red-800/80 text-red-300'
                }`}>
                  <div className="flex items-center gap-2">
                    {testResult.success ? (
                      <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400" />
                    ) : (
                      <AlertCircle className="w-4 h-4 shrink-0 text-red-400" />
                    )}
                    <span className="font-semibold">{testResult.message}</span>
                  </div>

                  {testResult.userInfo && (
                    <div className="pt-2 border-t border-white/10 grid grid-cols-2 gap-2 text-[11px] text-slate-300">
                      {testResult.userInfo.status && (
                        <div className="bg-black/30 p-1.5 rounded-lg border border-white/5">
                          <span className="text-slate-500 block text-[9px] uppercase font-bold">Estat</span>
                          <span className={testResult.userInfo.status === 'Active' ? 'text-emerald-400 font-semibold' : 'text-red-400 font-semibold'}>
                            {testResult.userInfo.status}
                          </span>
                        </div>
                      )}
                      {testResult.userInfo.expDateFormatted && (
                        <div className="bg-black/30 p-1.5 rounded-lg border border-white/5">
                          <span className="text-slate-500 block text-[9px] uppercase font-bold">Caducitat</span>
                          <span className="font-semibold text-white">{testResult.userInfo.expDateFormatted}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Modal Buttons */}
              <div className="flex items-center justify-between pt-3 border-t border-slate-800">
                <button
                  type="button"
                  onClick={handleTestSource}
                  disabled={isTestingSource}
                  className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-xl border border-slate-700 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isTestingSource ? 'animate-spin text-red-400' : ''}`} />
                  {isTestingSource ? 'Provant...' : 'Provar Connexió'}
                </button>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowSourceModal(false);
                      setEditingSource(null);
                    }}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-xl transition-colors"
                  >
                    Cancel·lar
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-red-600 hover:bg-red-700 active:scale-95 text-white text-xs font-bold rounded-xl shadow-lg shadow-red-600/30 transition-all"
                  >
                    {editingSource.id ? 'Desar Canvis' : 'Afegir i Sincronitzar'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Series Season / Episode Detail Modal */}
      {selectedSeriesDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in">
          <div className="bg-slate-900 border border-slate-700/80 rounded-2xl max-w-2xl w-full p-6 shadow-2xl relative max-h-[90vh] overflow-y-auto space-y-5">
            <div className="flex justify-between items-start">
              <div className="flex items-start gap-4">
                {selectedSeriesDetail.poster && (
                  <img
                    src={getProxiedImageUrl(selectedSeriesDetail.poster)}
                    alt=""
                    className="w-20 h-28 object-cover rounded-xl shadow-lg border border-slate-800 shrink-0"
                  />
                )}
                <div>
                  <h2 className="text-xl font-bold text-white leading-tight">
                    {selectedSeriesDetail.name}
                  </h2>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400 mt-1">
                    {selectedSeriesDetail.category && <span>{selectedSeriesDetail.category}</span>}
                    {selectedSeriesDetail.year && <span>• {selectedSeriesDetail.year}</span>}
                    {selectedSeriesDetail.rating && (
                      <span className="text-amber-400 font-bold">★ {selectedSeriesDetail.rating.toFixed(1)}</span>
                    )}
                  </div>
                  {selectedSeriesDetail.plot && (
                    <p className="text-xs text-slate-300 mt-2 line-clamp-3 leading-relaxed">
                      {selectedSeriesDetail.plot}
                    </p>
                  )}
                </div>
              </div>

              <button
                onClick={() => setSelectedSeriesDetail(null)}
                className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Seasons & Episodes List */}
            <div className="space-y-4 pt-2 border-t border-slate-800">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <Layers className="w-4 h-4 text-red-500" />
                Episodis Disponibles
              </h3>

              {loadingSeriesDetail ? (
                <div className="py-8 text-center text-slate-400 text-xs flex items-center justify-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin text-red-400" />
                  Carregant temporades i capítols...
                </div>
              ) : selectedSeriesDetail.seasons && selectedSeriesDetail.seasons.length > 0 ? (
                <div className="space-y-4">
                  {selectedSeriesDetail.seasons.map((season) => (
                    <div key={season.seasonNumber} className="space-y-2">
                      <div className="text-xs font-bold text-red-400 uppercase tracking-wider">
                        {season.name || `Temporada ${season.seasonNumber}`} ({season.episodes.length} capítols)
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {season.episodes.map((ep, epIdx) => {
                          const epPoster = ep.poster || (ep as any).movie_image;
                          const epPlot = ep.plot;
                          return (
                            <button
                              key={ep.id}
                              id={`iptv-series-ep-${season.seasonNumber}-${ep.episode || epIdx}`}
                              data-focusable="true"
                              onClick={() => {
                                setActivePlayingItem({
                                  ...ep,
                                  name: `${selectedSeriesDetail.name} - ${ep.title}`,
                                  seriesTitle: selectedSeriesDetail.name
                                });
                              }}
                              className="flex items-center gap-3 p-2.5 rounded-xl bg-slate-950/70 hover:bg-red-600/20 border border-slate-800/80 hover:border-red-500/40 text-left transition-all group focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-red-600/20"
                            >
                              {epPoster ? (
                                <div className="relative w-14 h-9 sm:w-16 sm:h-10 rounded-lg overflow-hidden shrink-0 bg-slate-800 border border-white/10">
                                  <img
                                    src={getProxiedImageUrl(epPoster)}
                                    alt=""
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                    onError={(e) => {
                                      (e.currentTarget as HTMLElement).style.display = 'none';
                                    }}
                                  />
                                  <span className="absolute bottom-0.5 left-0.5 px-1 py-0.2 rounded bg-black/80 font-mono text-[9px] font-bold text-white">
                                    {ep.episode}
                                  </span>
                                </div>
                              ) : (
                                <div className="w-8 h-8 rounded-lg bg-slate-800 group-hover:bg-red-600 flex items-center justify-center text-white shrink-0 text-xs font-bold transition-colors">
                                  <Play className="w-3.5 h-3.5 fill-current ml-0.5" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <MarqueeText
                                  text={ep.title}
                                  className="text-xs font-semibold text-white"
                                  activeClassName="group-hover:text-red-300"
                                />
                                <div className="flex items-center gap-2 text-[10px] text-slate-400 mt-0.5">
                                  <span>Temp. {ep.season}, Ep. {ep.episode}</span>
                                  {ep.duration && (
                                    <span>
                                      • {Math.round(Number(ep.duration) > 300 ? Number(ep.duration) / 60 : Number(ep.duration))} min
                                    </span>
                                  )}
                                </div>
                                {epPlot && (
                                  <p className="text-[10px] text-slate-400/90 line-clamp-1 mt-0.5 font-normal">
                                    {epPlot}
                                  </p>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-6 text-center text-slate-400 text-xs">
                  No s&apos;han trobat capítols per a aquesta sèrie.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Active Fullscreen Video Player */}
      {activePlayingItem && (
        <IptvPlayer
          item={activePlayingItem}
          allChannels={channels}
          onClose={() => setActivePlayingItem(null)}
          onSelectChannel={(ch) => setActivePlayingItem(ch)}
          isFavorite={isItemFavorite(activePlayingItem.id)}
          onToggleFavorite={() => handleToggleFav(activePlayingItem, activeTab === 'movies' ? 'movie' : 'live')}
        />
      )}
    </div>
  );
};
